import type { SurveyAnswerInput } from 'core/graphql';
import type { ISurveyQuestion } from './transformPendingSurveys';

/**
 * Typed form value per question kind (spec 0011 §3.1 value table):
 *   yes_no → boolean · text → string · rating → int 1–5 ·
 *   single_choice → option INDEX · multi_choice → int[] indexes · nps → int 1–10.
 * `null` / '' / [] / 0-rating mean "not answered yet".
 */
export type SurveyAnswerValue = boolean | string | number | number[] | null;

export type ISurveyFormValues = Record<string, SurveyAnswerValue>;

/** True when the widget is still in its untouched/cleared state. */
export const isSurveyAnswerEmpty = (
  question: ISurveyQuestion,
  value: SurveyAnswerValue | undefined,
): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  switch (question.questionType) {
    case 'text':
    case 'teacher_comment':
      return String(value).trim() === '';
    case 'rating':
    case 'teacher_rating':
      // antd Rate reports 0 when cleared — 0 is not a valid rating.
      return value === 0;
    case 'multi_choice':
      return Array.isArray(value) && value.length === 0;
    default:
      return false;
  }
};

/**
 * Builds the `answers` mutation payload from Formik values. The typed value is
 * JSON.stringify-ed HERE (the contract's `value` is a JSONString) — components
 * never touch the encoding. Unanswered questions are omitted entirely: for
 * optional ones that's the contract; for required ones the Yup schema blocks
 * submit first, and the backend re-validates with REQUIRED_MISSING anyway.
 */
export const buildSurveyAnswers = (
  questions: ISurveyQuestion[],
  values: ISurveyFormValues,
): SurveyAnswerInput[] =>
  questions.reduce<SurveyAnswerInput[]>((answers, question) => {
    const raw = values[question.id];
    if (isSurveyAnswerEmpty(question, raw)) {
      return answers;
    }
    const value =
      question.questionType === 'text' || question.questionType === 'teacher_comment'
        ? String(raw).trim()
        : raw;
    answers.push({ questionId: question.id, value: JSON.stringify(value) });
    return answers;
  }, []);
